import React, { useState, useRef, useEffect } from "react";
import { User } from "../types/api";
import { Layout } from "../components/layout/Layout";
import apiService from "../services/api";

type NavigateScreen =
  | "calendar"
  | "patients"
  | "tasks"
  | "internal-tasks"
  | "patient-page"
  | "patient"
  | "users"
  | "connections"
  | "billing"
  | "whatsapp";

interface BillingScreenProps {
  user: User;
  onLogout: () => void;
  onNavigate?: (screen: NavigateScreen, patientId?: string | number, meetingId?: string | number, phone?: string) => void;
  taskCount?: number;
  whatsappUnreadCount?: number;
}

type BillingTab = "obligation" | "rejects" | "clalit" | "maccabi";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Default selection = the last fully-ended month (1-based month).
// e.g. mid-June 2026 -> { year: 2026, month: 5 } (May).
function defaultYearMonth(): { year: number; month: number } {
  const now = new Date();
  let year = now.getFullYear();
  let month = now.getMonth(); // 0-based current month == 1-based previous month
  if (month === 0) {
    month = 12;
    year -= 1;
  }
  return { year, month };
}

// Extract a human-readable message from an Axios error, covering the various
// shapes the backend uses (`message`, FastAPI `detail` as string or `{ message }`,
// or `error`). Falls back to the provided default.
function extractErrorMessage(err: any, fallback: string): string {
  const data = err?.response?.data;
  return (
    data?.message ||
    data?.detail?.message ||
    (typeof data?.detail === "string" ? data.detail : undefined) ||
    data?.error ||
    err?.message ||
    fallback
  );
}

interface YearMonthValue {
  year: number;
  month: number;
}

const YearMonthPicker: React.FC<{
  value: YearMonthValue;
  onChange: (v: YearMonthValue) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let y = currentYear; y >= currentYear - 3; y--) years.push(y);

  return (
    <div className="flex items-center gap-3 mb-4">
      <select
        value={value.month}
        disabled={disabled}
        onChange={(e) => onChange({ ...value, month: Number(e.target.value) })}
        className="border border-gray-300 rounded-lg p-2 text-sm text-gray-700 bg-white disabled:opacity-50"
      >
        {MONTH_NAMES.map((name, idx) => (
          <option key={idx} value={idx + 1}>
            {name}
          </option>
        ))}
      </select>
      <select
        value={value.year}
        disabled={disabled}
        onChange={(e) => onChange({ ...value, year: Number(e.target.value) })}
        className="border border-gray-300 rounded-lg p-2 text-sm text-gray-700 bg-white disabled:opacity-50"
      >
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </div>
  );
};

const ymKey = (v: YearMonthValue) => `${v.year}-${v.month}`;

// Single dropdown populated from the months the backend reports as open for
// billing (previous + current, or current only when the previous is locked).
const MonthSelect: React.FC<{
  value: YearMonthValue | null;
  options: YearMonthValue[];
  onChange: (v: YearMonthValue) => void;
  loading?: boolean;
  disabled?: boolean;
}> = ({ value, options, onChange, loading, disabled }) => {
  if (loading) {
    return <p className="text-sm text-gray-500 mb-4">Loading available months…</p>;
  }
  if (options.length === 0) {
    return <p className="text-sm text-gray-500 mb-4">No billing months are currently available.</p>;
  }

  return (
    <div className="mb-4">
      <select
        value={value ? ymKey(value) : ""}
        disabled={disabled}
        onChange={(e) => {
          const picked = options.find((o) => ymKey(o) === e.target.value);
          if (picked) onChange(picked);
        }}
        className="border border-gray-300 rounded-lg p-2 text-sm text-gray-700 bg-white disabled:opacity-50"
      >
        {options.map((o) => (
          <option key={ymKey(o)} value={ymKey(o)}>
            {MONTH_NAMES[o.month - 1]} {o.year}
          </option>
        ))}
      </select>
    </div>
  );
};

type Message = { text: string; type: "success" | "error" } | null;

const primaryBtn = (disabled: boolean) => ({
  backgroundColor: disabled ? "#9CA3AF" : "#3A7380",
  cursor: disabled ? "not-allowed" : "pointer",
});

export const BillingScreen: React.FC<BillingScreenProps> = ({
  user,
  onLogout,
  onNavigate,
  taskCount,
  whatsappUnreadCount,
}) => {
  const [activeTab, setActiveTab] = useState<BillingTab>("obligation");

  const token = localStorage.getItem("token") || "";

  // ---- Tab 1: Clalit Obligation (existing upload) ----
  const [uploading, setUploading] = useState(false);
  const [obligationMessage, setObligationMessage] = useState<Message>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleObligationUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setObligationMessage(null);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = (e.target?.result as string).split(",")[1];
        try {
          await apiService.uploadClalitExcel({ token, filename: selectedFile.name, base64file: base64 });
          setObligationMessage({ text: "File uploaded successfully.", type: "success" });
          setSelectedFile(null);
          if (fileInputRef.current) fileInputRef.current.value = "";
        } catch (err: any) {
          setObligationMessage({ text: err?.response?.data?.message || "Upload failed.", type: "error" });
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(selectedFile);
    } catch {
      setObligationMessage({ text: "Failed to read file.", type: "error" });
      setUploading(false);
    }
  };

  // ---- Tab 2: Load Clalit Prev Month Rejects ----
  const [rejectsYM, setRejectsYM] = useState<YearMonthValue>(defaultYearMonth());
  const [rejectsFile, setRejectsFile] = useState<File | null>(null);
  const rejectsInputRef = useRef<HTMLInputElement>(null);
  const [rejectsRunning, setRejectsRunning] = useState(false);
  const [rejectsMessage, setRejectsMessage] = useState<Message>(null);
  const [notLocated, setNotLocated] = useState<{ patient_id: string; sum: number }[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const runRejects = async () => {
    if (!rejectsFile) return;
    setConfirmOpen(false);
    setRejectsRunning(true);
    setRejectsMessage(null);
    setNotLocated([]);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = (e.target?.result as string).split(",")[1];
        try {
          const res = await apiService.loadClalitRejects({
            token,
            filename: rejectsFile.name,
            base64file: base64,
            year: rejectsYM.year,
            month: rejectsYM.month,
          });
          if (res.success) {
            setRejectsMessage({ text: "Rejects loaded; matching records marked as unpaid.", type: "success" });
            setRejectsFile(null);
            if (rejectsInputRef.current) rejectsInputRef.current.value = "";
          } else if (res.error === "Rejects could not be found") {
            setRejectsMessage({ text: "Rejects could not be found. The records below were not located:", type: "error" });
            setNotLocated(res.not_located || []);
          } else {
            setRejectsMessage({ text: res.error || "Load failed.", type: "error" });
          }
        } catch (err: any) {
          setRejectsMessage({ text: err?.response?.data?.message || "Load failed.", type: "error" });
        } finally {
          setRejectsRunning(false);
        }
      };
      reader.readAsDataURL(rejectsFile);
    } catch {
      setRejectsMessage({ text: "Failed to read file.", type: "error" });
      setRejectsRunning(false);
    }
  };

  // ---- Tab 3 & 4: async billing runs ----
  const [clalitYM, setClalitYM] = useState<YearMonthValue | null>(null);
  const [clalitMonths, setClalitMonths] = useState<YearMonthValue[]>([]);
  const [clalitMonthsLoading, setClalitMonthsLoading] = useState(true);
  const [clalitRunning, setClalitRunning] = useState(false);
  const [clalitMessage, setClalitMessage] = useState<Message>(null);

  const [maccabiYM, setMaccabiYM] = useState<YearMonthValue | null>(null);
  const [maccabiMonths, setMaccabiMonths] = useState<YearMonthValue[]>([]);
  const [maccabiMonthsLoading, setMaccabiMonthsLoading] = useState(true);
  const [maccabiRunning, setMaccabiRunning] = useState(false);
  const [maccabiMessage, setMaccabiMessage] = useState<Message>(null);

  // Load the months that are still open for billing for each institution.
  // The backend drops the previous month from the list once it is locked.
  useEffect(() => {
    let cancelled = false;
    const load = async (
      institution: string,
      setMonths: (m: YearMonthValue[]) => void,
      setSelected: (v: YearMonthValue | null) => void,
      setLoading: (b: boolean) => void
    ) => {
      try {
        const months = await apiService.getAvailableBillingMonths({ token, institution });
        if (cancelled) return;
        setMonths(months);
        // Default to the latest available month (don't rely on response order).
        const latest = months.reduce<YearMonthValue | null>(
          (acc, m) => (!acc || m.year * 12 + m.month > acc.year * 12 + acc.month ? m : acc),
          null
        );
        setSelected(latest);
      } catch (err) {
        console.error(`[Billing] Failed to load available months for ${institution}:`, err);
        if (!cancelled) {
          setMonths([]);
          setSelected(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load("clalit", setClalitMonths, setClalitYM, setClalitMonthsLoading);
    load("maccabi", setMaccabiMonths, setMaccabiYM, setMaccabiMonthsLoading);
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Lock-previous confirmation: shown when the user runs the current month
  // while the previous month is still unlocked (i.e. still in the options).
  const [lockConfirm, setLockConfirm] = useState<
    { institution: "clalit" | "maccabi"; prev: YearMonthValue; run: YearMonthValue } | null
  >(null);
  const [lockPreviousChecked, setLockPreviousChecked] = useState(true);

  // The previous month is "not locked" while it is still offered as an option.
  const previousUnlocked = (options: YearMonthValue[]): YearMonthValue | null => {
    const prev = defaultYearMonth();
    return options.some((o) => ymKey(o) === ymKey(prev)) ? prev : null;
  };

  const handleRunClalit = () => {
    if (!clalitYM) return;
    const prev = previousUnlocked(clalitMonths);
    // Running the current month (not the previous) while previous is unlocked.
    if (prev && ymKey(clalitYM) !== ymKey(prev)) {
      setLockPreviousChecked(true);
      setLockConfirm({ institution: "clalit", prev, run: clalitYM });
      return;
    }
    runClalitBilling(false);
  };

  const handleRunMaccabi = () => {
    if (!maccabiYM) return;
    const prev = previousUnlocked(maccabiMonths);
    if (prev && ymKey(maccabiYM) !== ymKey(prev)) {
      setLockPreviousChecked(true);
      setLockConfirm({ institution: "maccabi", prev, run: maccabiYM });
      return;
    }
    runMaccabiBilling(false);
  };

  const runClalitBilling = async (lockPrevious: boolean) => {
    if (!clalitYM) return;
    setClalitRunning(true);
    setClalitMessage(null);
    try {
      await apiService.runClalitBilling({
        token,
        year: clalitYM.year,
        month: clalitYM.month,
        ...(lockPrevious ? { lock_previous: true } : {}),
      });
      setClalitMessage({ text: `A report email will be sent to ${user.email} when the run completes.`, type: "success" });
    } catch (err: any) {
      setClalitMessage({ text: extractErrorMessage(err, "Failed to start billing run."), type: "error" });
    } finally {
      setClalitRunning(false);
    }
  };

  const runMaccabiBilling = async (lockPrevious: boolean) => {
    if (!maccabiYM) return;
    setMaccabiRunning(true);
    setMaccabiMessage(null);
    try {
      await apiService.runMaccabiBilling({
        token,
        year: maccabiYM.year,
        month: maccabiYM.month,
        ...(lockPrevious ? { lock_previous: true } : {}),
      });
      setMaccabiMessage({ text: `A report email will be sent to ${user.email} when the run completes.`, type: "success" });
    } catch (err: any) {
      setMaccabiMessage({ text: extractErrorMessage(err, "Failed to start billing run."), type: "error" });
    } finally {
      setMaccabiRunning(false);
    }
  };

  const tabs: { id: BillingTab; label: string }[] = [
    { id: "obligation", label: "Clalit Obligation" },
    { id: "rejects", label: "Load Clalit Prev Month Rejects" },
    { id: "clalit", label: "Run Clalit Monthly Billing" },
    { id: "maccabi", label: "Run Maccabi Monthly Billing" },
  ];

  const renderMessage = (message: Message) =>
    message ? (
      <p className={`mt-4 text-sm font-medium ${message.type === "success" ? "text-green-600" : "text-red-600"}`}>
        {message.text}
      </p>
    ) : null;

  return (
    <Layout
      userName={user.name}
      onLogout={onLogout}
      activeMenuItem="billing"
      onNavigate={onNavigate}
      user={user}
      taskCount={taskCount}
      whatsappUnreadCount={whatsappUnreadCount}
    >
      <div className="p-8">
        <h1 className="text-2xl font-semibold text-gray-800 mb-6">Billing</h1>

        {/* Tabs */}
        <div className="flex flex-nowrap gap-2 border-b border-gray-200 mb-6">
          {tabs.map((tab) => (
            <button
              data-ai-id="billing-tab"
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2.5 text-base font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${
                activeTab === tab.id
                  ? "text-[#3A7380] border-[#3A7380]"
                  : "text-gray-700 border-transparent hover:text-gray-900"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content (kept narrower than the tab row) */}
        <div className="max-w-2xl">

        {/* Tab 1: Clalit Obligation */}
        {activeTab === "obligation" && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <p className="text-base text-gray-700 mb-4">Upload the obligation Excel file:</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => {
                setSelectedFile(e.target.files?.[0] || null);
                setObligationMessage(null);
              }}
              className="block w-full text-sm text-gray-700 border border-gray-300 rounded-lg p-2 mb-4 cursor-pointer"
            />
            {selectedFile && (
              <p className="text-sm text-gray-600 mb-4">
                Selected: <span className="font-medium">{selectedFile.name}</span>
              </p>
            )}
            <button
              onClick={handleObligationUpload}
              disabled={!selectedFile || uploading}
              className="w-full py-2 px-4 rounded-lg text-white font-medium transition-colors"
              style={primaryBtn(!selectedFile || uploading)}
            >
              {uploading ? "Uploading..." : "Upload"}
            </button>
            {renderMessage(obligationMessage)}
          </div>
        )}

        {/* Tab 2: Load Clalit Prev Month Rejects */}
        {activeTab === "rejects" && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <p className="text-base text-gray-700 mb-4">
              Upload the Clalit rejects file (XLSX). Matching records will be marked as unpaid.
            </p>
            <YearMonthPicker value={rejectsYM} onChange={setRejectsYM} disabled />
            <input
              ref={rejectsInputRef}
              type="file"
              accept=".xlsx"
              onChange={(e) => {
                setRejectsFile(e.target.files?.[0] || null);
                setRejectsMessage(null);
                setNotLocated([]);
              }}
              className="block w-full text-sm text-gray-700 border border-gray-300 rounded-lg p-2 mb-4 cursor-pointer"
            />
            {rejectsFile && (
              <p className="text-sm text-gray-600 mb-4">
                Selected: <span className="font-medium">{rejectsFile.name}</span>
              </p>
            )}
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={!rejectsFile || rejectsRunning}
              className="w-full py-2 px-4 rounded-lg text-white font-medium transition-colors"
              style={primaryBtn(!rejectsFile || rejectsRunning)}
            >
              {rejectsRunning ? "Loading..." : "Load Rejects"}
            </button>
            {renderMessage(rejectsMessage)}
            {notLocated.length > 0 && (
              <div className="mt-3 border border-red-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-red-50 text-red-700">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Patient ID</th>
                      <th className="text-left px-3 py-2 font-medium">Sum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notLocated.map((entry, idx) => (
                      <tr key={idx} className="border-t border-red-100">
                        <td className="px-3 py-2 text-gray-700">{entry.patient_id}</td>
                        <td className="px-3 py-2 text-gray-700">{entry.sum}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Tab 3: Run Clalit Monthly Billing */}
        {activeTab === "clalit" && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <p className="text-base text-gray-700 mb-4">
              Select the billing month and start the Clalit monthly billing run. The generated files will be emailed to you at {user.email}.
            </p>
            <MonthSelect
              value={clalitYM}
              options={clalitMonths}
              onChange={setClalitYM}
              loading={clalitMonthsLoading}
              disabled={clalitRunning}
            />
            <button
              onClick={handleRunClalit}
              disabled={clalitRunning || !clalitYM}
              className="w-full py-2 px-4 rounded-lg text-white font-medium transition-colors"
              style={primaryBtn(clalitRunning || !clalitYM)}
            >
              {clalitRunning ? "Starting..." : "Run Clalit Billing"}
            </button>
            {renderMessage(clalitMessage)}
          </div>
        )}

        {/* Tab 4: Run Maccabi Monthly Billing */}
        {activeTab === "maccabi" && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <p className="text-base text-gray-700 mb-4">
              Select the billing month and start the Maccabi monthly billing run. The generated files will be emailed to you at {user.email}.
            </p>
            <MonthSelect
              value={maccabiYM}
              options={maccabiMonths}
              onChange={setMaccabiYM}
              loading={maccabiMonthsLoading}
              disabled={maccabiRunning}
            />
            <button
              onClick={handleRunMaccabi}
              disabled={maccabiRunning || !maccabiYM}
              className="w-full py-2 px-4 rounded-lg text-white font-medium transition-colors"
              style={primaryBtn(maccabiRunning || !maccabiYM)}
            >
              {maccabiRunning ? "Starting..." : "Run Maccabi Billing"}
            </button>
            {renderMessage(maccabiMessage)}
          </div>
        )}
        </div>
      </div>

      {/* Confirmation dialog for loading rejects */}
      {confirmOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Mark records as unpaid?</h2>
            <p className="text-sm text-gray-600 mb-6">
              This will load the rejects for{" "}
              <span className="font-medium">{MONTH_NAMES[rejectsYM.month - 1]} {rejectsYM.year}</span> and mark the
              matching records as <span className="font-semibold">unpaid</span>.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={runRejects}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg"
                style={{ backgroundColor: "#3A7380" }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lock-previous confirmation for running the current month */}
      {lockConfirm && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Previous Billing Month - {MONTH_NAMES[lockConfirm.prev.month - 1]} {lockConfirm.prev.year} is not locked.
            </h2>
            <p className="text-sm text-gray-600 mb-4">Do you want to Lock it now?</p>
            <label className="flex items-center gap-2 text-sm text-gray-700 mb-6 cursor-pointer">
              <input
                type="checkbox"
                checked={lockPreviousChecked}
                onChange={(e) => setLockPreviousChecked(e.target.checked)}
                className="h-4 w-4"
              />
              Lock Billing Month {MONTH_NAMES[lockConfirm.prev.month - 1]} {lockConfirm.prev.year}
            </label>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setLockConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const { institution } = lockConfirm;
                  const lockPrevious = lockPreviousChecked;
                  setLockConfirm(null);
                  if (institution === "clalit") runClalitBilling(lockPrevious);
                  else runMaccabiBilling(lockPrevious);
                }}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg"
                style={{ backgroundColor: "#3A7380" }}
              >
                Run {MONTH_NAMES[lockConfirm.run.month - 1]} {lockConfirm.run.year} Billing
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};
